import isEqual from 'lodash/isEqual';
import some from 'lodash/some';

import type { RootState } from '@/store';

import { selectIsAgencyDataAvailable } from '@/ducks/fm/selectors';
import { selectIsMnvv } from '@/ducks/mnvv/selectors';
import { ensureArray } from '@/helpers/util/misc';

import { config } from '../config';
import { selectFilters, selectFiltersDependencies } from '../selectors';
import { type FiltersKey, type FiltersTypes, type GetFilterValuesProps } from '../types';
import { getSearchParamsKeys, getSearchParamsValue } from './primitive';

export const getSerializedValue = <T>({
  key,
  searchParams,
  state,
  value,
}: {
  key: FiltersKey;
  searchParams: URLSearchParams;
  state: RootState;
  value: T;
}) => {
  const serializeValue = config[key]?.serializeValue;
  return typeof serializeValue === 'function'
    ? serializeValue!(value, { searchParams, state })
    : (value?.toString() ?? null);
};

export const getUpdatedKeys = (filters: FiltersTypes, updatedFilters: FiltersTypes) => {
  if (filters === updatedFilters) {
    return [];
  }

  return (Object.keys(filters) as FiltersKey[]).reduce<FiltersKey[]>(
    (updatedKeys, key) => (isEqual(filters[key], updatedFilters[key]) ? updatedKeys : [...updatedKeys, key]),
    [],
  );
};

const getDependencies = ({ searchParams, state }: GetFilterValuesProps) => {
  return JSON.stringify({
    isFm: selectIsAgencyDataAvailable(state),
    isMnvv: selectIsMnvv(state),
    searchParams: searchParams.toString(),
  });
};

export const addExplicitValuesToURL = ({ state }: { state: RootState }) => {
  let updated = false;
  const filters = selectFilters(state);
  const newSearchParams = new URLSearchParams(window.location.search);
  (Object.keys(config) as FiltersKey[]).forEach((key) => {
    if (config[key].explicit) {
      const value = getSearchParamsValue(key, newSearchParams);
      const newValue = getSerializedValue({ key, searchParams: newSearchParams, state, value: filters?.[key] });
      const searchParamsKeys = getSearchParamsKeys(key);
      if (!value || newValue !== value) {
        updated = true;

        searchParamsKeys.forEach((searchParamsKey) => {
          newSearchParams.delete(searchParamsKey);
        });

        if (newValue) {
          newSearchParams.append(searchParamsKeys[0]!, newValue as string);
        }
      }
    }
  });

  if (updated) {
    const q = newSearchParams.toString();
    history.replaceState(null, '', `${window.location.pathname}${q ? `?${q}` : ''}`);
  }
};

export const getFilterValues = (props: GetFilterValuesProps, isForced?: boolean) => {
  const { searchParams, state } = props;

  const dependencies = getDependencies(props);
  if (!isForced && dependencies === selectFiltersDependencies(state)) {
    return state.filters;
  }

  const values = Object.entries(config).reduce((filterValues, [key, { getValue }]) => {
    let value: FiltersTypes[keyof FiltersTypes] | null = getSearchParamsValue(key as FiltersKey, searchParams);
    if (getValue && typeof getValue === 'function') {
      value = getValue({ ...props, value });
    }
    return {
      ...filterValues,
      [key]: value,
    };
  }, {} as FiltersTypes);

  return {
    dependencies,
    values,
  };
};

export const getInvalidFiltersSearchParamsKeys = (props: GetFilterValuesProps) => {
  return (Object.keys(config) as FiltersKey[]).reduce<string[]>((keys, key) => {
    const isValueValidForURL = config[key].isValueValidForURL;
    const value: FiltersTypes[keyof FiltersTypes] | null = getSearchParamsValue(key as FiltersKey, props.searchParams);
    const isValid =
      !isValueValidForURL || (typeof isValueValidForURL === 'function' && isValueValidForURL({ ...props, value }));
    return isValid ? keys : [...keys, ...getSearchParamsKeys(key)];
  }, []);
};

export const isFiltersApplied = (keys: FiltersKey | FiltersKey[], searchParams: URLSearchParams): boolean => {
  return (ensureArray(keys) as FiltersKey[] | undefined)!.some(
    (key) => getSearchParamsValue(key, searchParams) !== null,
  );
};

export const checkIfFilterOverridden = (key: FiltersKey, searchParams: URLSearchParams) => {
  return Boolean(some(getSearchParamsKeys(key), (key) => searchParams.has(key)));
};
